import { c as _c } from "react/compiler-runtime";
import { DialogContent, DialogDescription, DialogHeader, DialogTitle, Dialog as ShadDialog } from '#components/atoms/dialog';
import { cn } from '#lib/utils';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import clsx from 'clsx';
import { ComponentProps, FC, ReactNode, useState } from 'react';
import { Button } from './Button';
type DialogSize = 'small' | 'medium' | 'large' | 'fluid';
type DialogScroll = 'body' | 'viewport' | 'none';
type DialogButtonVariant = 'primary' | 'danger';
interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept?: () => void;
  title: string;
  children: ReactNode;
  size?: DialogSize;
  scroll?: DialogScroll;
  showCloseButton?: boolean;
  acceptLabel?: string;
  closeLabel?: string;
  acceptButtonVariant?: DialogButtonVariant;
  declineLabel?: string;
  onDecline?: () => void;
  declineButtonVariant?: DialogButtonVariant;
  anchor?: 'bottom';
}
export const Dialog: FC<DialogProps> = ({
  isOpen,
  onClose,
  onAccept,
  title,
  children,
  size = 'small',
  showCloseButton = false,
  acceptLabel = 'Salvesta',
  closeLabel = 'Katkesta',
  acceptButtonVariant = 'primary',
  declineLabel,
  onDecline,
  declineButtonVariant = 'danger',
  anchor
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const handleAccept = async () => {
    setIsLoading(true);
    await onAccept?.();
    setIsLoading(false);
  };
  if (!isOpen) return null;
  const dialogClasses = clsx('tds-dialog', {
    [`tds-dialog--${size}`]: size
  });
  return <ShadDialog onOpenChange={open => {
    if (showCloseButton && !open) {
      onClose();
    }
  }} open={isOpen}>
      <DialogContent className={cn(dialogClasses, 'w-[90%] md:w-auto', {
      'bottom-4': anchor === 'bottom',
      'top-1/2 -translate-y-1/2': anchor !== 'bottom'
    })}>
        <DialogHeader className="tds-dialog__header">
          <DialogDescription asChild className="sr-only">
            <div>{children}</div>
          </DialogDescription>
          <DialogTitle className="tds-dialog__title">{title}</DialogTitle>
          {showCloseButton && <DialogPrimitive.Close asChild>
              <Button type="button" variant="icon-neutral" className="tds-notification-close" compact aria-label={closeLabel} onClick={onClose}>
                <DialogCloseIcon />
              </Button>
            </DialogPrimitive.Close>}
        </DialogHeader>
        <div id="dialog-content" className="tds-dialog__body">
          {children}
        </div>
        <div className="tds-dialog__footer">
          <Button type="button" variant="secondary-neutral" aria-label={closeLabel} onClick={onClose} disabled={isLoading} className="text-nowrap">
            {closeLabel}
          </Button>
          {onDecline && <Button type="button" variant={declineButtonVariant} aria-label={declineLabel} onClick={onDecline} disabled={isLoading} className="text-nowrap">
              {declineLabel}
            </Button>}
          {onAccept && <Button type="button" variant={acceptButtonVariant} aria-label={acceptLabel} onClick={handleAccept} isLoading={isLoading} className="text-nowrap">
              {acceptLabel}
            </Button>}
        </div>
      </DialogContent>
    </ShadDialog>;
};
const DialogCloseIcon: FC<ComponentProps<'svg'>> = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <path fillRule="evenodd" clipRule="evenodd" d="M9.99974 11.1785L4.75566 16.4226L3.57715 15.2441L8.82123 9.99998L3.57715 4.7559L4.75566 3.57739L9.99974 8.82147L15.2438 3.57739L16.4223 4.7559L11.1782 9.99998L16.4223 15.2441L15.2438 16.4226L9.99974 11.1785Z" fill="#131416" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" {...props}>{t0}</svg>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};